import { createSlice } from '@reduxjs/toolkit'
import { guid, isTrueStringMatch } from 'apps/builder/lib/utils'
import { deterministicGuid } from 'apps/builder/lib/hash'
import {
  copySheet,
  copyTableOrSimpleLoopSheet,
  splitSheet,
  importFormData,
  deleteEntry,
  copyEntry,
} from 'apps/formsCreationKit/store/actions/sharedActions'
import { actions as clientActions } from '@gocanvas/client'
const { loadFormData } = clientActions

import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'

const copyOperations = (
  operations,
  newSheetIdLookup,
  copiedSheetEntries,
  seed
) => {
  const copiedCalculationEntryGuids = copiedSheetEntries.reduce(
    (acc, entry) => {
      if (entry.type === 'Calculation') acc.push(entry.guid)
      return acc
    },
    []
  )

  if (copiedCalculationEntryGuids.length === 0) return []

  return operations.reduce((acc, operation) => {
    const {
      parentEntryGuid,
      parentEntrySheetId,
      entry: entryGuid,
      sheet_id: entrySheetId,
    } = operation
    if (copiedCalculationEntryGuids.includes(entryGuid)) {
      const updatedOperation = {
        ...operation,
        id: guid(),
        sheet_id: newSheetIdLookup[entrySheetId],
        entry: deterministicGuid(entryGuid, seed),
      }

      // If the operand is on the copied sheet, we need to update it to point to the new sheet/entry copy
      const needsParentEntryUpdate = Object.keys(newSheetIdLookup).find(
        copiedSheetId => isTrueStringMatch(copiedSheetId, parentEntrySheetId)
      )
      if (needsParentEntryUpdate) {
        updatedOperation.parentEntrySheetId =
          newSheetIdLookup[parentEntrySheetId]
        updatedOperation.parentEntryGuid = deterministicGuid(
          parentEntryGuid,
          seed
        )
      }

      acc.push(updatedOperation)
    }

    return acc
  }, [])
}

export const deleteOperations = (operations, deletedEntryGuid) =>
  operations.filter(
    ({ entry: entryGuid, parentEntryGuid }) =>
      entryGuid != deletedEntryGuid && parentEntryGuid != deletedEntryGuid
  )

export const initialState = []

export const operationsSliceExtraReducers = builder => {
  builder
    .addCase(loadFormData, (state, action) => {
      const { operations } = action.payload
      return operations
    })
    .addCase(importFormData, (state, action) => {
      const { operations = [] } = action.payload
      return [...state, ...operations]
    })
    .addCase(copyEntry, (state, action) => {
      const { guid: originalGuid, seed } = action.payload
      const newOperations = state.reduce((acc, operation) => {
        const { entry: entryGuid } = operation
        if (isTrueStringMatch(entryGuid, originalGuid)) {
          acc.push({
            ...operation,
            id: guid(),
            entry: deterministicGuid(entryGuid, seed),
          })
        }
        return acc
      }, [])
      return [...state, ...newOperations]
    })
    .addCase(deleteEntry, (state, action) => {
      const { guid: deletedEntryGuid } = action.payload
      return deleteOperations(state, deletedEntryGuid)
    })
    .addCase(copySheet, (state, action) => {
      const {
        id: copiedSheetId,
        newId,
        sheetEntries = [],
        seed,
      } = action.payload

      const copiedOperations = copyOperations(
        state,
        { [copiedSheetId]: newId },
        sheetEntries,
        seed
      )

      return [...state, ...copiedOperations]
    })
    .addCase(copyTableOrSimpleLoopSheet, (state, action) => {
      const {
        parentSheetId,
        sheetsToCopy = [],
        entriesToCopy = [],
        newSheetGuid,
        newMultiSheetGuid,
        seed,
      } = action.payload

      const childSheetId = sheetsToCopy.find(
        id => !isTrueStringMatch(parentSheetId, id)
      )

      const newSheetIdLookup = {
        [parentSheetId]: newSheetGuid,
        [childSheetId]: newMultiSheetGuid,
      }

      const copiedOperations = copyOperations(
        state,
        newSheetIdLookup,
        entriesToCopy,
        seed
      )

      return [...state, ...copiedOperations]
    })
    .addCase(splitSheet, (state, action) => {
      const { newId, sheetEntries, position } = action.payload

      const entriesSplitToNewSheet = sheetEntries.reduce((acc, entry) => {
        if (entry.position > position) acc.push(entry.guid)
        return acc
      }, [])

      return state.map(operation => {
        const { parentEntryGuid, entry: entryGuid } = operation
        const updatedOperation = { ...operation }

        if (entriesSplitToNewSheet.includes(parentEntryGuid)) {
          updatedOperation.parentEntrySheetId = newId
        }
        if (entriesSplitToNewSheet.includes(entryGuid)) {
          updatedOperation.sheet_id = newId
        }

        return updatedOperation
      })
    })
    .addCase(saveApp.fulfilled, (state, action) => {
      const { entities } = action.payload
      const { operations } = entities
      return operations
    })
}

export const operationsSliceConfig = {
  name: 'operations',
  initialState: initialState,
  reducers: {
    addOperation(state, action) {
      return [...state, action.payload]
    },
    updateOperation(state, action) {
      const { id } = action.payload
      return state.map(condition => {
        if (id !== condition.id) return condition
        return {
          ...condition,
          ...action.payload,
        }
      })
    },
    deleteOperation(state, action) {
      const id = action.payload
      return state.filter(condition => condition.id != id)
    },
    deleteEmptyOperations(state, action) {
      const { entryGuid } = action.payload
      return state.filter(operation => {
        const { entry, parentEntryGuid, value } = operation
        if (entry === entryGuid && !parentEntryGuid && !value) {
          // Entries that are missing both a parentEntryGuid and value
          // are considered empty and should be deleted
          return false
        }

        return true
      })
    },
  },
  extraReducers: operationsSliceExtraReducers,
}

export const operationsSlice = createSlice(operationsSliceConfig)
export const { actions: operationsActions, reducer: operationsReducer } =
  operationsSlice
